import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { EmployeesService, employees_ProvisionLoginRequest, employees_LinkLoginRequest } from '@/api';
import type {
  models_Employee,
  employees_CreateEmployeeRequest,
  employees_UpdateEmployeeRequest,
  EmployeePickerEntry,
} from '@/types';
import type {
  ProvisionLoginResponse,
  LinkLoginResponse,
  ReissueLoginResponse,
  RevokeLoginResponse,
  LoginStatusResponse,
} from '@/types';
import { toast } from 'sonner';
import { invalidateEmployeeAuditQueries } from './useEmployeeAudit';
import { format } from 'date-fns';
import {
  retryOnConcurrentModification,
  isEntityStatusDerived,
} from '@/utils/retryOnConcurrentModification';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { extractBackendError, translateBackendError } from '@/lib/translateBackendError';
import { useModuleGate } from './useAccess';
import { EMPLOYEE_DETAIL_READ_MODULES, EMPLOYEE_LIST_MODULES } from '@/lib/modules';

// Query keys for employees
export const employeeKeys = {
  all: ['employees'] as const,
  lists: () => [...employeeKeys.all, 'list'] as const,
  list: (filters: Record<string, string | number | boolean>) => [...employeeKeys.lists(), { filters }] as const,
  counts: () => [...employeeKeys.all, 'count'] as const,
  count: (status: string) => [...employeeKeys.counts(), status] as const,
  // Under `lists()`, so every employee mutation that invalidates the lists also
  // refreshes the picker. Tenant-scoped: the active orgId is part of the key.
  picker: (orgId: string | null | undefined) =>
    [...employeeKeys.lists(), 'picker', { orgId: orgId ?? '' }] as const,
  details: () => [...employeeKeys.all, 'detail'] as const,
  detail: (id: string) => [...employeeKeys.details(), id] as const,
  loginStatuses: () => [...employeeKeys.all, 'loginStatus'] as const,
  // Tenant-scoped: the login status is derived per-org (org membership), so the
  // active orgId is part of the key to avoid serving another tenant's status.
  loginStatus: (orgId: string | null | undefined, id: string) =>
    [...employeeKeys.loginStatuses(), orgId ?? 'no-org', id] as const,
};

// The backend (ParsePaginationParams) actually accepts a limit up to 1000; this
// 100 is a deliberate client-side cap for the list fetch, not a backend limit.
// A page-limited list is NOT a substitute for a count, which is why the
// "Active employees" KPI uses useEmployeeCount (B6) rather than counting a
// loaded page.
const MAX_EMPLOYEE_LIMIT = 100;

// Hook to fetch all employees
export function useEmployees(limit = 50, offset = 0) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  // EMP_LIST: the server admits the list for these modules only (#1670).
  const canReadEmployees = useModuleGate(EMPLOYEE_LIST_MODULES);
  const clampedLimit = Math.min(limit, MAX_EMPLOYEE_LIMIT);

  return useQuery({
    queryKey: employeeKeys.list({ limit: clampedLimit, offset }),
    queryFn: () => EmployeesService.getApiV1Employees(clampedLimit, offset),
    enabled: isSignedIn && isAuthReady && canReadEmployees,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof Error && (
        error.message.includes('401') || 
        error.message.includes('403') || 
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// The server's per-request maximum (ParsePaginationParams). One request covers
// the whole staff of any realistic organisation, so the Staff tables, pickers
// and id lookups share ONE request instead of paging 100 at a time (#2194 A7).
const EMPLOYEE_DIRECTORY_LIMIT = 1000;

// A full page may hide more rows, so the hook fails rather than show a staff
// list with people missing. Deterministic: never retried.
class EmployeeDirectoryTruncatedError extends Error {
  constructor() {
    super(`useAllEmployees: ${EMPLOYEE_DIRECTORY_LIMIT} or more employees; refusing to return a possibly truncated list`);
    this.name = 'EmployeeDirectoryTruncatedError';
  }
}

// Hook to fetch ALL employees. `useEmployees` clamps to MAX_EMPLOYEE_LIMIT, so
// every list, picker and id lookup that must see the whole staff uses this one.
export function useAllEmployees() {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();
  // EMP_LIST: the server admits the list for these modules only (#1670).
  const canReadEmployees = useModuleGate(EMPLOYEE_LIST_MODULES);

  return useQuery({
    queryKey: [...employeeKeys.lists(), 'all', { orgId: orgId ?? '' }],
    queryFn: async () => {
      const rows = (await EmployeesService.getApiV1Employees(EMPLOYEE_DIRECTORY_LIMIT, 0)) ?? [];
      if (rows.length >= EMPLOYEE_DIRECTORY_LIMIT) {
        throw new EmployeeDirectoryTruncatedError();
      }
      return rows;
    },
    enabled: isSignedIn && isAuthReady && canReadEmployees,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error instanceof EmployeeDirectoryTruncatedError) {
        return false;
      }
      // Don't retry on 401/403 errors
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

/**
 * The slim any-module employee picker (issue #1688): `{id, firstname, lastname,
 * status, groupIds}` of every non-deleted employee, ordered by last name.
 *
 * `GET /employees/picker` admits any module, so this hook is NOT module-gated:
 * every page that only needs an employee's NAME (a picker, an author or
 * organiser label, an actor on a timeline) reads it instead of the EMP_LIST
 * full records. A consumer that needs HR fields (contact, contract, login)
 * keeps `useAllEmployees` and its gate.
 *
 * The response is the whole staff in one request (no paging): the name
 * directories must resolve every id.
 */
export function useEmployeePicker() {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  return useQuery({
    ...employeePickerQueryOptions(orgId),
    enabled: !!isSignedIn && isAuthReady,
  });
}

/**
 * Key, fetcher and cache policy of the picker. Every hook that reads the picker
 * spreads these, so they share ONE cache entry and one request (a `select` may
 * reshape the rows per consumer).
 */
export function employeePickerQueryOptions(orgId: string | null | undefined) {
  return {
    queryKey: employeeKeys.picker(orgId),
    queryFn: async (): Promise<EmployeePickerEntry[]> => {
      const response = await EmployeesService.getApiV1EmployeesPicker();
      return response.employees ?? [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount: number, error: unknown) => {
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    },
  };
}

/** "Firstname Lastname" of a picker row; '' when both parts are blank. */
export function formatEmployeeName(
  employee: { firstname?: string; lastname?: string } | undefined | null,
): string {
  if (!employee) return '';
  return [employee.firstname, employee.lastname].filter(Boolean).join(' ').trim();
}

/**
 * Employee id -> "Firstname Lastname", built from the picker. An id missing
 * from the map is not an employee of the organisation (or was deleted); the
 * caller renders its own fallback, never the raw id. `namesByClerkUserId` maps
 * the Clerk user id that audit columns (`created_by`) carry to the same name.
 */
export function useEmployeePickerNames() {
  const query = useEmployeePicker();
  const names = useMemo(() => employeePickerNameMap(query.data), [query.data]);
  const namesByClerkUserId = useMemo(() => employeePickerClerkNameMap(query.data), [query.data]);
  return { ...query, names, namesByClerkUserId };
}

/** Clerk user id -> "Firstname Lastname"; rows without a login or a name are left out. */
export function employeePickerClerkNameMap(
  entries: ReadonlyArray<EmployeePickerEntry> | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const employee of entries ?? []) {
    const name = formatEmployeeName(employee);
    if (employee.clerkUserId && name) map.set(employee.clerkUserId, name);
  }
  return map;
}

/** Id -> "Firstname Lastname" of the picker rows; rows without a name are left out. */
export function employeePickerNameMap(
  entries: ReadonlyArray<EmployeePickerEntry> | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const employee of entries ?? []) {
    const name = formatEmployeeName(employee);
    if (employee.id && name) map.set(employee.id, name);
  }
  return map;
}

/**
 * Whether a picker that ASSIGNS someone (shift, progress entry, organiser,
 * instructor, key worker, tour) may offer this employee: ACTIVE or UPCOMING.
 * Departed (INACTIVE) employees stay in the name directories so history keeps
 * its names, but are not offered for new work. A row without a status (an
 * older payload) counts as selectable. Callers keep the CURRENT value visible
 * even when it is no longer selectable, so an existing assignment never blanks.
 */
export function isSelectableEmployee(employee: { status?: string }): boolean {
  return !employee.status || employee.status === 'ACTIVE' || employee.status === 'UPCOMING';
}

/** Case-insensitive name match on "first last" and "last first". A blank query matches everyone. */
export function matchesEmployeeName(
  employee: { firstname?: string; lastname?: string },
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const first = (employee.firstname ?? '').toLowerCase();
  const last = (employee.lastname ?? '').toLowerCase();
  return `${first} ${last}`.includes(needle) || `${last} ${first}`.includes(needle);
}

/**
 * Name search over the picker for async selects: resolves the picker from the
 * shared cache (fetching it once when cold) and filters it in the browser, so
 * it needs no EMP_LIST module. At most `limit` rows, like the former
 * server-side search page. `selectableOnly` drops departed employees
 * (`isSelectableEmployee`) for pickers that assign someone.
 */
export function useEmployeePickerSearch(limit = 50, options: { selectableOnly?: boolean } = {}) {
  const queryClient = useQueryClient();
  const { orgId } = useAuthContext();
  const selectableOnly = options.selectableOnly ?? false;
  return useCallback(
    async (query: string): Promise<EmployeePickerEntry[]> => {
      const rows = await queryClient.fetchQuery(employeePickerQueryOptions(orgId));
      return rows
        .filter((employee) => (!selectableOnly || isSelectableEmployee(employee)) && matchesEmployeeName(employee, query))
        .slice(0, limit);
    },
    [queryClient, orgId, limit, selectableOnly],
  );
}

/**
 * Server-side count of employees, optionally filtered by a comma-separated
 * status list (e.g. "ACTIVE"). Backs the Analytics "Active employees" KPI so it
 * reflects the whole org rather than a single loaded page (B6).
 */
export function useEmployeeCount(status = '') {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  // EMP_LIST: the server admits the count for these modules only (#1670).
  const canReadEmployees = useModuleGate(EMPLOYEE_LIST_MODULES);

  return useQuery({
    queryKey: employeeKeys.count(status),
    queryFn: () => EmployeesService.getApiV1EmployeesCount(status || undefined),
    enabled: isSignedIn && isAuthReady && canReadEmployees,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to fetch a single employee
export function useEmployee(id: string) {
  // `GET /employees/:id` admits the employees module only (#1670).
  const canReadEmployeeDetail = useModuleGate(EMPLOYEE_DETAIL_READ_MODULES);
  return useQuery({
    queryKey: employeeKeys.detail(id),
    queryFn: () => EmployeesService.getApiV1Employees1(id),
    enabled: !!id && canReadEmployeeDetail,
  });
}

// Hook to fetch employee with relations
export function useEmployeeWithRelations(id: string) {
  return useQuery({
    queryKey: [...employeeKeys.detail(id), 'with-relations'],
    queryFn: () => EmployeesService.getApiV1EmployeesWithRelations(id),
    enabled: !!id,
  });
}

// Hook to search employees by name
export function useSearchEmployees(name: string, limit = 50, offset = 0) {
  return useQuery({
    queryKey: [...employeeKeys.lists(), 'search', { name, limit, offset }],
    queryFn: () => EmployeesService.getApiV1EmployeesSearch(name, limit, offset),
    enabled: !!name,
  });
}

/**
 * The picker row of the employee belonging to the signed-in user, when there
 * is one.
 *
 * There is no `/employees/me`: the link is `clerkUserId`, so this matches the
 * Clerk user id against the any-module picker (#1688), which every role can
 * read. It exists to DEFAULT a picker, never to decide one — a miss (the user
 * is an admin with no employee record) yields `undefined`, which the caller
 * must render as "nothing preselected" rather than substituting a guessed id.
 */
export function useCurrentEmployee() {
  const { userId } = useAuthContext();
  const { data, isLoading, isError } = useEmployeePicker();

  const employee = userId
    ? (data ?? []).find((candidate) => candidate.clerkUserId === userId)
    : undefined;

  return { employee, isLoading, isError };
}

// Hook to create an employee
export function useCreateEmployee() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (employee: employees_CreateEmployeeRequest) => {
      return EmployeesService.postApiV1Employees(employee);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      // Invalidate detail cache so it fetches fresh with labels
      if (data?.id) {
        queryClient.invalidateQueries({ queryKey: employeeKeys.detail(data.id) });
      }
      toast.success(i18n._('common.success.created'));
    },
    onError: (error: Error) => {
      // A stable Code (e.g. DIRECT_HEAD_CYCLE) is translated and wins over the
      // message-substring heuristics below, which only ever saw raw strings.
      const { code } = extractBackendError(error);
      if (code) {
        toast.error(translateBackendError(code, i18n, { id: 'common.error.createFailed' }));
        return;
      }

      // Handle specific error types
      const errorMessage = error.message || "Unknown error";
      const lowerMessage = errorMessage.toLowerCase();
      
      // Check for unique email constraint (case-insensitive)
      if (lowerMessage.includes("duplicate key") || 
          lowerMessage.includes("unique constraint") ||
          lowerMessage.includes("email already exists") ||
          lowerMessage.includes("email")) {
        toast.error(i18n._('employees.toast.duplicateEmail'));
        return;
      }
      
      // Handle validation errors (case-insensitive)
      if (lowerMessage.includes("validation") || lowerMessage.includes("invalid")) {
        toast.error(i18n._('common.error.validationFailed'));
        return;
      }
      
      // Generic error fallback
      toast.error(i18n._('common.error.createFailed'));
    },
  });
}

// Hook to update an employee
export function useUpdateEmployee() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: employees_UpdateEmployeeRequest }) => {
      // Issue #1284 / decision D5: the retry lives INSIDE mutationFn, not at the
      // call site. TanStack Query runs onError before it rejects the first
      // attempt, so a call-site wrapper toasts an error the admin then sees
      // followed by a success toast for the very same save. A 422
      // ENTITY_STATUS_DERIVED is never retried (decision D10).
      return retryOnConcurrentModification(() =>
        EmployeesService.putApiV1Employees(id, data),
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      // Invalidate detail cache to refetch with labels (update response doesn't include labels)
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(variables.id) });
      // Invalidate entity-labels cache for this employee
      queryClient.invalidateQueries({ queryKey: ['entity-labels', 'employees', variables.id] });
      // Invalidate audit queries to ensure fresh audit data after employee update
      invalidateEmployeeAuditQueries(queryClient, variables.id);
      // The hook owns the success toast; callers do not toast again.
      toast.success(i18n._(msg`Employee updated successfully!`));
    },
    onError: (error: unknown) => {
      console.error('Failed to update employee:', error);
      // Issue #1224 / decision D11: the edit form owns the 422 message -- it
      // translates it AND refetches so the status control re-renders read-only.
      // Toasting here too would show the same sentence twice.
      if (isEntityStatusDerived(error)) {
        return;
      }
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to update employee`));
    },
  });
}

// Hook to delete an employee
export function useDeleteEmployee() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => EmployeesService.deleteApiV1Employees(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      // Invalidate audit queries to ensure fresh audit data after employee deletion
      invalidateEmployeeAuditQueries(queryClient, variables.id);
      toast.success(i18n._(msg`Employee deleted successfully!`));
    },
    onError: (error: unknown) => {
      console.error('Failed to delete employee:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to delete employee`));
    },
  });
}

// Hook to update a single field of an employee (for table editing)
export function useUpdateEmployeeField() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async ({ 
      id, 
      field, 
      value 
    }: { 
      id: string; 
      field: keyof models_Employee; 
      value: models_Employee[keyof models_Employee] 
    }) => {
      // Format date fields to match the employee form behavior
      let processedValue = value;
      if (field === 'birthdate' && value instanceof Date) {
        // Format date to RFC3339 with midnight UTC time, matching employeeSchema.ts
        processedValue = format(value, "yyyy-MM-dd'T'00:00:00'Z'");
      }
      
      // Create update object with only the changed field
      const updateData = { [field]: processedValue };
      // Issue #1224 / decision D9: the inline cell is a SECOND write path for
      // `status`, bypassing the edit form. It gets the same single 409 retry as
      // the form, so an inline edit that races the 20-minute derivation tick does
      // not surface a raw error. A 422 ENTITY_STATUS_DERIVED is never retried --
      // the helper rethrows it and onError below translates and refetches.
      return retryOnConcurrentModification(() =>
        EmployeesService.putApiV1Employees(id, updateData),
      );
    },
    onSuccess: (data, variables) => {
      // Issue #1296: the PUT response is NOT decorated with `statusDerived` --
      // only the GET and list paths are (employee_handler.go). Writing the raw
      // body into the detail cache would blank the flag, and EditEmployeeWrapper
      // feeds that cached row into the form as a prop, so the next form render
      // would show a read-only status control for an employee the admin owns.
      // Invalidate instead: the refetch goes through the decorated GET.
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(variables.id) });
      
      // Update the employee in any list queries - fix the query key pattern
      queryClient.setQueriesData(
        { queryKey: ['employees', 'list'] }, // This will match all list queries regardless of filters
        (oldData: models_Employee[] | undefined) => {
          if (!oldData) return oldData;
          // Issue #1296: this merge is OPTIMISTIC feedback for the edited field
          // only. It cannot refresh `statusDerived` -- the PUT response omits the
          // key, so the spread leaves the row's previous value in place. The list
          // invalidation below is what makes the flag correct.
          return oldData.map((employee) =>
            employee.id === variables.id ? { ...employee, ...data } : employee
          );
        }
      );
      
      // Issue #1296: mirrors useUpdateChildField.onSuccess -- the refetched rows
      // come back through the decorated list path, so `statusDerived` is fresh.
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      
      // Invalidate audit queries to ensure fresh audit data after field update
      invalidateEmployeeAuditQueries(queryClient, variables.id);
      
      // Show success message
      toast.success(i18n._('common.success.updated'));
    },
    onError: (error: Error, variables) => {
      // Issue #1224: a 422 ENTITY_STATUS_DERIVED means the entity gained a window
      // while the table was open, so the cell must stop offering an edit. Show
      // the translated message and invalidate the list so the row's
      // `statusDerived` refreshes. The rethrow stays -- the table still renders
      // its own inline error; the toast and the invalidation are added BEFORE it,
      // not instead of it.
      if (isEntityStatusDerived(error)) {
        const { code } = extractBackendError(error);
        toast.error(
          translateBackendError(code, i18n, msg`Failed to update employee`),
        );
        queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
        queryClient.invalidateQueries({
          queryKey: employeeKeys.detail(variables.id),
        });
      }
      throw error; // Re-throw to be handled by the table component
    },
  });
}

// ---------------------------------------------------------------------------
// Login provisioning (issue #354)
// ---------------------------------------------------------------------------

// Invalidate the employee detail + list caches so the login-status badge/role
// reflect immediately after a provisioning action.
function invalidateEmployeeLoginCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  employeeId: string,
) {
  queryClient.invalidateQueries({ queryKey: employeeKeys.detail(employeeId) });
  queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
  // Refresh the detail-view "Signed in" badge after provisioning/re-issue.
  queryClient.invalidateQueries({ queryKey: employeeKeys.loginStatuses() });
}

/**
 * Detail-only login status (issue #354). Fetches a best-effort "Signed in"
 * flag from a single Clerk lookup; only enabled when a login exists (so the
 * Clerk call is skipped entirely for un-provisioned employees) and the caller
 * opts in via `enabled`. The list view derives its badge from employee fields
 * without this call.
 */
export function useEmployeeLoginStatus(id: string, enabled: boolean) {
  const { orgId } = useAuthContext();

  return useQuery<LoginStatusResponse>({
    queryKey: employeeKeys.loginStatus(orgId, id),
    queryFn: () => EmployeesService.getApiV1EmployeesLoginStatus(id),
    enabled: enabled && !!id,
    // The signed-in flag changes rarely; avoid hammering Clerk on every render.
    staleTime: 60_000,
  });
}

/**
 * Create a Clerk login for an employee. Returns the one-time initial password
 * in the mutation result so the panel can show it exactly once — the password
 * is NEVER written to the query cache.
 */
export function useProvisionEmployeeLogin() {
  const queryClient = useQueryClient();

  return useMutation<ProvisionLoginResponse, unknown, { id: string; role: 'admin' | 'staff' }>({
    mutationFn: ({ id, role }) =>
      EmployeesService.postApiV1EmployeesProvisionLogin(id, {
        role: role as employees_ProvisionLoginRequest.role,
      }),
    onSuccess: (_data, variables) => {
      invalidateEmployeeLoginCaches(queryClient, variables.id);
    },
    // Intentionally no toast here: the panel surfaces success (the one-time
    // password) and translates errors inline via translateBackendError.
  });
}

/**
 * Link an existing Clerk user (matched by the employee's email) to the
 * employee. No password is returned (the existing account keeps its password).
 */
export function useLinkEmployeeLogin() {
  const queryClient = useQueryClient();

  return useMutation<LinkLoginResponse, unknown, { id: string; role: 'admin' | 'staff' }>({
    mutationFn: ({ id, role }) =>
      EmployeesService.postApiV1EmployeesLinkLogin(id, {
        role: role as employees_LinkLoginRequest.role,
      }),
    onSuccess: (_data, variables) => {
      invalidateEmployeeLoginCaches(queryClient, variables.id);
    },
  });
}

/**
 * Re-issue a fresh one-time password for a linked employee. Returns the new
 * password in the mutation result (shown once; never cached).
 */
export function useReissueEmployeeLogin() {
  const queryClient = useQueryClient();

  return useMutation<ReissueLoginResponse, unknown, { id: string }>({
    mutationFn: ({ id }) => EmployeesService.postApiV1EmployeesReissueLogin(id),
    onSuccess: (_data, variables) => {
      invalidateEmployeeLoginCaches(queryClient, variables.id);
    },
  });
}

/**
 * Revoke an employee's login (issue #1255). Deletes the Clerk organization
 * membership and clears the local link; the Clerk USER is never deleted.
 *
 * On success both the employee query and the login-status query refetch, so the
 * badge returns to "No login" without a manual reload.
 */
export function useRevokeEmployeeLogin() {
  const queryClient = useQueryClient();

  return useMutation<RevokeLoginResponse, unknown, { id: string }>({
    mutationFn: ({ id }) => EmployeesService.postApiV1EmployeesRevokeLogin(id),
    onSuccess: (_data, variables) => {
      invalidateEmployeeLoginCaches(queryClient, variables.id);
    },
    // Intentionally no toast here: the panel translates errors inline via
    // translateBackendError, matching the sibling login mutations.
  });
}
